import { capitalize, upperFirst } from 'lodash';
import React, { useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useStores } from '../../context/context';
import sharedStyles from '../../styles/shared.module.css';
import DeviceList from './components/DeviceList';
import styles from './DevicePage.module.css';

function DeviceListPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { deviceStore, ticketStore } = useStores();
  const [devices, setDevices] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [isManufacturerOnlyRoute, setIsManufacturerOnlyRoute] = useState(false);
  const [searchPlaceholder, setSearchPlaceholder] = useState(null);
  const [highlightInput, setHighlightInput] = useState(false);
  const searchInputRef = useRef(null);
  
  // Check if this is a manufacturer-only route (e.g., /Apple)
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const isManufacturerOnly = pathSegments.length === 1 && !pathSegments[0].includes('-repair');
  
  const type = isManufacturerOnly ? null : location.pathname.replace('/', '').split('-')[0];
  const typeID =
    type === 'phone'
      ? 1
      : type === 'tablet'
      ? 2
      : type === 'laptop'
      ? 3
      : type === 'console'
      ? 4
      : type === 'graphics'
      ? 5
      : 0;
  
  // params.manufacturer is set by React Router when route matches /:manufacturer
  // For manufacturer-only routes like /Apple, params.manufacturer will be 'Apple'
  // For routes like /phone-repair/Apple, params.manufacturer will also be 'Apple'
  const manufacturer = params.manufacturer;

  const loadDevices = async () => {
    // Recalculate path segments from current location to avoid stale closures
    const currentPathSegments = location.pathname.split('/').filter(Boolean);
    const currentIsManufacturerOnly = currentPathSegments.length === 1 && !currentPathSegments[0].includes('-repair');
        
    if (currentIsManufacturerOnly) {
      // For manufacturer-only routes, we need to load all device types
      // and then filter by manufacturer
      const allDevices = await deviceStore.getAllDevices();
      
      // Get the manufacturer from params (set by React Router) or fallback to path segment
      // For route /:manufacturer (e.g., /Apple), React Router sets params.manufacturer = 'Apple'
      // params.manufacturer comes from the route definition: path: '/:manufacturer'
      const manufacturerName = params.manufacturer || currentPathSegments[0];

      setIsManufacturerOnlyRoute(true);
      
      // Group devices by manufacturer name (since same manufacturer appears in multiple device types)
      const manufacturerMap = new Map();
      
      allDevices.forEach((manufacturerDevices) => {
        const key = manufacturerDevices.manufacturer.toLowerCase();
        if (manufacturerMap.has(key)) {
          // Combine devices from different device types
          const existing = manufacturerMap.get(key);
          existing.devices = [...existing.devices, ...manufacturerDevices.devices];
        } else {
          // First occurrence of this manufacturer
          manufacturerMap.set(key, {
            ...manufacturerDevices,
            devices: [...manufacturerDevices.devices]
          });
        }
      });
      
      // Convert map back to array
      const groupedDevices = Array.from(manufacturerMap.values());
      
      // Find exact match
      const matchingManufacturer = groupedDevices.find(
        (manufacturerDevices) => 
          manufacturerDevices.manufacturer.toLowerCase() === manufacturerName.toLowerCase()
      );
      
      
      if (matchingManufacturer) {
        setDevices([matchingManufacturer]);
      } else {
        // If no exact match, try partial match
        const partialMatches = groupedDevices.filter(
          (manufacturerDevices) =>
            manufacturerDevices.manufacturer.toLowerCase().includes(manufacturerName.toLowerCase())
          );
        setDevices(partialMatches);
      }
    } else {
      // Regular device type route
      await deviceStore.getDevices(typeID);

      const params = new URLSearchParams(location.search);

      if (params.get('s')) {
        const value = params.get('s').replace('_', ' ');
        setSearchValue(value);
      } else if (manufacturer) {
        onManufacturerSearch(upperFirst(manufacturer));
      } else {
        setDevices(deviceStore.devices);
      }
    }
  };

  useEffect(() => {
    loadDevices();
  }, [location.pathname, type]);

  useEffect(() => {
    ticketStore.clearTicket();
  }, []);

  useEffect(() => {
    if (searchValue.length > 0) {
      onDeviceSearchChange(searchValue);
    } else {
      onDeviceSearchChange('');
    }
  }, [searchValue]);

  const onDeviceSearchChange = (value) => {
    if (value.length === 0) {
      // For manufacturer-only routes, we need to reload the original filtered devices
      if (isManufacturerOnlyRoute) {
        loadDevices();
      } else {
        setDevices(deviceStore.devices);
      }
      return;
    }
    
    const regex = new RegExp(value, 'i');
    let searchDevices = [];
    
    // Use the current devices state for searching
    const devicesToSearch = devices || deviceStore.devices;
    
    devicesToSearch.forEach((manufacturerDevices) => {
      const matchingDevices = manufacturerDevices.devices.filter((device) =>
        regex.test(device.device)
      );

      // Return search results based on manufacturer name or device name
      if (
        manufacturerDevices.manufacturer
          .toLowerCase()
          .includes(value.toLowerCase())
      ) {
        searchDevices.push({ ...manufacturerDevices });
      } else if (matchingDevices.length > 0) {
        searchDevices.push({
          ...manufacturerDevices,
          devices: matchingDevices,
        });
      }
    });
    
    // Don't add "Other devices" section anymore - the "Other device" button handles this
    // The button text will update based on searchValue
    
    setDevices(searchDevices);
  };

  const onManufacturerSearch = (value) => {
    const manufacturer = deviceStore.devices.filter(
      (manufacturer) => manufacturer.manufacturer.toLowerCase() === value.toLowerCase()
    );
    if (manufacturer.length > 0) {
      setDevices(manufacturer);
    } else {
      setDevices(deviceStore.devices);
    }
  };

  const onDeviceClick = (device) => {
    // Handle "Other device" case (deviceID: -1) - bespoke devices
    if (device.deviceID === -1) {
      // For bespoke devices, always use 'other' as the device type
      // and keep the manufacturer from the current page context
      const deviceName = device.device.replaceAll(' ', '_').replaceAll('/', '~');
      
      // Keep the manufacturer from the current page (device.manufacturer is set by DeviceList component)
      // If we're on a manufacturer-only route like /Apple, this will be "Apple"
      // Don't change it to 'Other' - keep the actual manufacturer name
      const manufacturer = device.manufacturer === 'Other devices' 
        ? (params.manufacturer || pathSegments[0] || 'Other') 
        : device.manufacturer;
      
      const url = `/other-repair/${manufacturer}/${deviceName}?deviceID=-1`;
      
      navigate(url);
      return;
    }
    
    // For manufacturer-only routes, we need to determine the device type
    // We can do this by checking which device type the device belongs to
    let deviceType = type;
    
    if (!deviceType && isManufacturerOnlyRoute) {
      // Try to determine device type from the device data or context
      // This is a fallback - ideally the device data should include device type info
      deviceType = 'phone'; // Default fallback
    }
    
    const url = `/${deviceType}-repair/${device.manufacturer}/${device.device
      .replaceAll(' ', '_')
      .replaceAll('/', '~')}`;

    navigate(url);
  };

  return (
    <div className={sharedStyles.container}>
      <Helmet>
        <title>
          {isManufacturerOnlyRoute 
            ? `Repatch - ${upperFirst(pathSegments[0])} Devices`
            : `Repatch - ${upperFirst(type)} Repair`
          }
        </title>
      </Helmet>
      <h1 className={styles.pageTitle}>
        {isManufacturerOnlyRoute 
          ? `${upperFirst(pathSegments[0])} Devices`
          : `${capitalize(type)} Models`
        }
      </h1>
      <input
        ref={searchInputRef}
        className={styles.searchInput}
        placeholder={
          searchPlaceholder || (
            isManufacturerOnlyRoute 
              ? `Type in device model...`
              : `Type in ${type} model...`
          )
        }
        onChange={(event) => {
          setSearchValue(event.target.value);
          // Remove highlight when user starts typing
          if (highlightInput) {
            setHighlightInput(false);
          }
        }}
        value={searchValue}
        style={{
          border: highlightInput ? '3px solid var(--main-brand-dark)' : undefined,
          outline: highlightInput ? 'none' : undefined,
          transition: 'border 0.3s ease-in-out'
        }}
      />
      {!deviceStore.isFetching && devices ? (
        <DeviceList 
          devices={devices} 
          onDeviceClick={onDeviceClick}
          onOtherDeviceClick={() => {
            setSearchPlaceholder('Add your device here');
            setHighlightInput(true);
            // Focus the input after a brief delay to ensure it's rendered
            setTimeout(() => {
              searchInputRef.current?.focus();
            }, 0);
            // Remove highlight after 3 seconds or when user starts typing
            setTimeout(() => {
              setHighlightInput(false);
            }, 3000);
          }}
          searchValue={searchValue}
          currentManufacturer={params.manufacturer || (isManufacturerOnlyRoute ? pathSegments[0] : null)}
        />
      ) : (
        <div style={{ alignSelf: 'center', marginTop: 20 }}>
          <LoadingSpinner />
        </div>
      )}
    </div>
  );
}

export default DeviceListPage;
